/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigation, useParams } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { supportsVariantDeepLink } from '@/lib/product/product-utils';
import { useNavigate } from '@/hooks/use-navigate';
import { routes, routeHref } from '@/route-paths';

interface UseCurrentVariantProps {
    product: ShopperProducts.schemas['Product'];
    isChildProduct?: boolean;
    /**
     * Optional override forwarded to {@link useSelectedVariations}. Modal contexts pass their
     * local selection state here to avoid reading from / writing to the page URL.
     */
    selectionsOverride?: Record<string, string>;
}

/**
 * Determines the current product variant based on selected variation attributes and URL parameters.
 * Automatically syncs the variant selection with URL query parameters for shareable product links.
 *
 * @example Basic usage in ProductView
 * ```tsx
 * function ProductView({ product }) {
 *   const currentVariant = useCurrentVariant({ product });
 *
 *   return (
 *     <div>
 *       {currentVariant && (
 *         <p>Selected: {currentVariant.productId}</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @param props - Configuration object
 * @param props.product - Product with variants array
 * @param props.isChildProduct - If true, prevents URL updates (useful for product sets/bundles)
 * @returns The matching variant object or undefined if no exact match is found
 */
export function useCurrentVariant({ product, isChildProduct = false, selectionsOverride }: UseCurrentVariantProps) {
    const navigate = useNavigate();
    const navigation = useNavigation();
    const location = useLocation();
    const routeParams = useParams();

    // Use useSelectedVariations to get URL-aware selected attributes (or override-driven in modal usage)
    const selectedAttributes = useSelectedVariations({ product, isChildProduct, selectionsOverride });

    const variants = useMemo(() => product?.variants ?? [], [product]);

    // Find current variant based on selected attributes or URL param
    const currentVariant = useMemo(() => {
        const potentialVariants = variants.filter(
            ({ variationValues }) =>
                variationValues &&
                Object.keys(selectedAttributes).every((key) => {
                    return variationValues[key] === selectedAttributes[key];
                })
        );
        // If there's only 1 match, then we have narrowed down the true variant (meaning all of the variation attributes have been selected)
        return potentialVariants.length === 1 ? potentialVariants[0] : undefined;
    }, [selectedAttributes, variants]);

    // Sync the URL to the resolved variant by writing the SKU into the `/product/<master>/<sku>` path
    // segment (UCR-3032 — this used to be a `?pid=` search param). Skip when an override is supplied
    // (modal usage) so swatch changes there don't pollute the page URL or trigger route revalidation.
    //
    // The navigation.state === 'idle' guard avoids a race during the swatch click: while the
    // NavLink's nav to the new selection is in flight, useSelectedVariations has already flipped
    // optimistically, so currentVariant points at the new variant — but `location` is still the
    // canonical (old) URL. Building a new URL from that snapshot would supersede the in-flight nav
    // with one derived from stale state. Defer the sync until the navigation has settled.
    useEffect(() => {
        if (navigation.state !== 'idle' || isChildProduct || selectionsOverride) return;
        if (!product?.id || !currentVariant?.productId) return;

        // Same "where applicable" rule the listing tiles use, so the two can't drift: a single-variant
        // master (and a bundle or set) keeps its bare `/product/<name>` URL. `currentVariant` is still
        // returned either way, so add-to-cart submits the right SKU regardless of what the URL says.
        if (!supportsVariantDeepLink(product)) return;

        // The master segment as it actually appears in the URL — authoritative, and avoids having to
        // re-derive it from the loaded product (which may itself be the variant).
        const masterSegment = routeParams.productId;
        if (!masterSegment) return;

        // Already canonical, or the URL addresses the variant directly as its master segment (in which
        // case appending the same id again would produce `/product/<sku>/<sku>`).
        if (routeParams.variantId === currentVariant.productId || masterSegment === currentVariant.productId) return;

        // The SKU segment fully encodes the selection, so the per-attribute params it replaces are
        // dropped. Everything else in the query string (attribution, etc.) is preserved.
        const search = new URLSearchParams(location.search);
        product.variationAttributes?.forEach((attribute) => {
            if (attribute.id) search.delete(attribute.id);
        });

        const target = routeHref(routes.product, {
            productId: masterSegment,
            variantId: currentVariant.productId,
        });
        const query = search.toString();

        void navigate(query ? `${target}?${query}` : target, { replace: true, preventScrollReset: true });
    }, [
        currentVariant,
        product,
        location.search,
        routeParams.productId,
        routeParams.variantId,
        navigate,
        isChildProduct,
        selectionsOverride,
        navigation.state,
    ]);

    return currentVariant;
}
